/* eslint-disable operator-assignment */

import type { IAugmentedJQuery, IController, IDocumentService, IScope } from "angular";
import { isDefined } from "angular";
import Hammer from "hammerjs";

import type { ISvgService } from "App/Modules/Gui";
import type { IPoint } from "App/Modules/Models";

type DragEvent = (args: { position: IPoint }) => void;

export default class SvgDraggableDirectiveController implements IController {
    public static $inject = ["$scope", "$element", "$document", "mmSvgService"];

    public tap: DragEvent;

    public dragging: DragEvent;

    public dragStart: DragEvent;

    public dragStop: DragEvent;

    public editable?: boolean;

    public position?: IPoint;

    public hammerManager: HammerManager;

    private startX = 0.0;

    private startY = 0.0;

    private svg: SVGSVGElement;

    constructor(
        private readonly $scope: IScope,
        private readonly $element: IAugmentedJQuery,
        private readonly $document: IDocumentService,
        private readonly svgService: ISvgService,
    ) {}

    public $onInit() {
        this.svg = this.svgService.getSvgRoot(this.$element[0] as any as SVGElement);

        this.$element.attr("class", "mm-svg-draggable");

        if (this.position) {
            this.$element[0].setAttribute("transform", `translate(${this.position.x},${this.position.y})`);
        } else {
            this.position = {
                x: 0.0,
                y: 0.0,
            };
        }

        if (!isDefined(this.editable)) {
            this.editable = true;
        }

        this.hammerManager = new Hammer.Manager(this.$element[0], { enable: true });
        this.hammerManager.add(new Hammer.Pan({ direction: Hammer.DIRECTION_ALL }));
        this.hammerManager.add(new Hammer.Tap());
        this.hammerManager.on("tap", (event: any) => this.elementTap(event));

        this.updateEditable();
        this.$scope.$watch("$ctrl.editable", () => this.updateEditable());
    }

    private updateEditable() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const hammerOptions = (this.hammerManager as any).options as HammerOptions;

        if (this.editable) {
            this.$element.attr("class", "mm-svg-draggable mm-svg-draggable-editable");

            this.hammerManager.on("panstart", (event: any) => this.elementPanStart(event));
            this.hammerManager.on("panmove", (event: any) => this.elementPanMove(event));
            this.hammerManager.on("panend", (event: any) => this.elementPanEnd(event));
        } else {
            this.$element.attr("class", "mm-svg-draggable");

            this.hammerManager.off("panstart panmove panend");
        }
    }

    private getMousePosition(event: HammerInput): IPoint {
        return {
            x: event.center.x,
            y: event.center.y,
        };
    }

    private getMouseStartPosition(event: HammerInput): IPoint {
        return {
            x: event.center.x - event.deltaX,
            y: event.center.y - event.deltaY,
        };
    }

    private elementTap(event: HammerInput) {
        const mousePosition = this.getMouseStartPosition(event);
        const p = this.svgService.pointToSvgCoordinates(this.svg, mousePosition);

        this.startX = p.x - this.position.x;
        this.startY = p.y - this.position.y;

        this.$scope.$apply(() => {
            this.tap({
                position: { x: p.x, y: p.y },
            });
        });
    }

    private elementPanStart(event: HammerInput) {
        const mousePosition = this.getMouseStartPosition(event);
        const p = this.svgService.pointToSvgCoordinates(this.svg, mousePosition);

        this.startX = p.x - this.position.x;
        this.startY = p.y - this.position.y;

        this.$scope.$apply(() => {
            this.dragStart({ position: { x: p.x, y: p.y } });
        });
    }

    private elementPanMove(event: HammerInput) {
        const mousePosition = this.getMousePosition(event);
        const newPosition: SVGPoint = this.svgService.pointToSvgCoordinates(this.svg, mousePosition);

        newPosition.x = newPosition.x - this.startX;
        newPosition.y = newPosition.y - this.startY;

        this.svgService.constrainToSvgBounds(this.svg, newPosition);

        this.$scope.$apply(() => {
            this.position.x = newPosition.x;
            this.position.y = newPosition.y;

            this.$element[0].setAttribute("transform", `translate(${newPosition.x},${newPosition.y})`);

            this.dragging({ position: { x: newPosition.x, y: newPosition.y } });
        });
    }

    private elementPanEnd(event: HammerInput) {
        const mousePosition = this.getMousePosition(event);
        const newPosition: SVGPoint = this.svgService.pointToSvgCoordinates(this.svg, mousePosition);

        newPosition.x = newPosition.x - this.startX;
        newPosition.y = newPosition.y - this.startY;

        // const bBox = (this.$element[0] as any).getBBox();

        this.svgService.constrainToSvgBounds(this.svg, newPosition);

        this.$scope.$apply(() => {
            this.position.x = newPosition.x;
            this.position.y = newPosition.y;

            this.$element[0].setAttribute("transform", `translate(${newPosition.x},${newPosition.y})`);

            this.dragStop({ position: { x: newPosition.x, y: newPosition.y } });
        });
    }
}

// eslint-enable
